import type { ExerciseContent, ExerciseResponse } from '../schemas/exercise'
import { diffWords, matchAnswer, normalizeText, type WordDiffEntry } from '../text/normalize'

/**
 * Pure graders. This module is the reason `packages/shared` compiles without
 * DOM or Workers types: the exact same code grades an answer instantly in her
 * browser (including offline) and authoritatively on the Worker. The server's
 * verdict is what gets persisted; the client's is what makes the UI feel
 * immediate.
 */

export type GradeStatus = 'correct' | 'partial' | 'incorrect' | 'deferred'

export interface GradeFeedback {
  message?: string
  /** The canonical right answer, shown after a miss. */
  expected?: string
  explanation?: string
  /** Right word, wrong accent — worth calling out without failing her. */
  accentSlip?: boolean
  diff?: WordDiffEntry[]
  /** Per-blank / per-pair outcomes for partial-credit rendering. */
  parts?: Array<'correct' | 'accent' | 'wrong'>
}

export interface GradeResult {
  correct: boolean
  /** 0..1. Partial credit on multi-part exercises. */
  score: number
  status: GradeStatus
  feedback: GradeFeedback
}

const ACCENT_HINT = 'Right word — watch the accent.'

function result(
  correct: boolean,
  score: number,
  feedback: GradeFeedback,
  status?: GradeStatus,
): GradeResult {
  return {
    correct,
    score: Math.max(0, Math.min(1, score)),
    status: status ?? (correct ? 'correct' : score > 0 ? 'partial' : 'incorrect'),
    feedback,
  }
}

/**
 * Grades one exercise. Throws only if content and response types disagree,
 * which is a bug, not user error.
 */
export function grade(content: ExerciseContent, response: ExerciseResponse): GradeResult {
  if (content.type !== response.type) {
    throw new Error(
      `Response type "${response.type}" does not match exercise type "${content.type}".`,
    )
  }

  switch (content.type) {
    case 'multiple_choice': {
      const res = response as Extract<ExerciseResponse, { type: 'multiple_choice' }>
      const correct = res.optionId === content.correctId
      const expected = content.options.find((o) => o.id === content.correctId)?.text
      return result(correct, correct ? 1 : 0, {
        expected: correct ? undefined : expected,
        explanation: content.explanation,
      })
    }

    case 'fill_blank': {
      const res = response as Extract<ExerciseResponse, { type: 'fill_blank' }>
      const tolerant = content.accentTolerant === true
      const parts: Array<'correct' | 'accent' | 'wrong'> = []
      let hits = 0
      let anyAccentSlip = false

      content.blanks.forEach((blank, i) => {
        const m = matchAnswer(res.values[i] ?? '', blank.accepted)
        if (m.kind === 'exact') {
          parts.push('correct')
          hits++
        } else if (m.kind === 'accent') {
          anyAccentSlip = true
          if (tolerant) {
            parts.push('accent')
            hits++
          } else {
            parts.push('wrong')
          }
        } else {
          parts.push('wrong')
        }
      })

      const score = hits / content.blanks.length
      const correct = hits === content.blanks.length
      return result(correct, score, {
        parts,
        accentSlip: anyAccentSlip,
        message: correct && anyAccentSlip ? ACCENT_HINT : undefined,
        expected: correct
          ? undefined
          : content.blanks.map((b) => b.accepted[0] ?? '').join(' · '),
      })
    }

    case 'translation': {
      const res = response as Extract<ExerciseResponse, { type: 'translation' }>
      const tolerant = content.accentTolerant === true
      const m = matchAnswer(res.text, content.accepted)
      const canonical = content.accepted[0] ?? ''

      if (m.kind === 'exact') return result(true, 1, {})
      if (m.kind === 'accent' && tolerant) {
        return result(true, 1, { accentSlip: true, message: ACCENT_HINT })
      }
      return result(false, 0, {
        expected: canonical,
        accentSlip: m.accentSlip,
        message: m.accentSlip ? ACCENT_HINT : undefined,
        diff: diffWords(canonical, res.text),
      })
    }

    case 'word_order': {
      const res = response as Extract<ExerciseResponse, { type: 'word_order' }>
      const given = res.order.join(',')
      const valid = [content.correctOrder, ...(content.alternates ?? [])].map((o) => o.join(','))
      const correct = valid.includes(given)

      // Partial credit by how many tokens sit in the right slot, so a
      // one-word slip does not read the same as total confusion.
      const positional =
        content.correctOrder.filter((idx, i) => res.order[i] === idx).length /
        content.correctOrder.length

      return result(correct, correct ? 1 : positional, {
        expected: correct
          ? undefined
          : content.correctOrder.map((i) => content.tokens[i] ?? '').join(' '),
      })
    }

    case 'matching': {
      const res = response as Extract<ExerciseResponse, { type: 'matching' }>
      const parts: Array<'correct' | 'accent' | 'wrong'> = []
      let hits = 0

      content.pairs.forEach((_, i) => {
        const chosen = res.pairs.find((p) => p.leftIndex === i)
        const ok = chosen?.rightIndex === i
        parts.push(ok ? 'correct' : 'wrong')
        if (ok) hits++
      })

      const score = hits / content.pairs.length
      return result(hits === content.pairs.length, score, { parts })
    }

    case 'listening': {
      const res = response as Extract<ExerciseResponse, { type: 'listening' }>

      if (content.mode === 'choose') {
        const correct = !!res.optionId && res.optionId === content.correctId
        return result(correct, correct ? 1 : 0, {
          expected: correct
            ? undefined
            : content.options?.find((o) => o.id === content.correctId)?.text,
        })
      }

      const accepted = content.accepted ?? []
      const canonical = accepted[0] ?? ''
      const m = matchAnswer(res.text ?? '', accepted)
      if (m.kind === 'exact') return result(true, 1, {})
      if (m.kind === 'accent' && content.accentTolerant === true) {
        return result(true, 1, { accentSlip: true, message: ACCENT_HINT })
      }
      return result(false, 0, {
        expected: canonical,
        accentSlip: m.accentSlip,
        diff: diffWords(canonical, res.text ?? ''),
      })
    }

    case 'speaking':
    case 'conversation':
      // Needs speech-to-text / the tutor model, which are Phase 3 and run
      // server-side and asynchronously. Recording the attempt is not the same
      // as scoring it, so we say so rather than inventing a score.
      return result(false, 0, { message: 'Awaiting review.' }, 'deferred')
  }
}

/** Whether an exercise can be graded on-device (and therefore offline). */
export function isLocallyGradable(type: ExerciseContent['type']): boolean {
  return type !== 'speaking' && type !== 'conversation'
}

/**
 * Phase 3 helper, kept here so the scoring rule lives with the other graders.
 * Reports word accuracy and completeness only — never accent quality, which
 * this pipeline genuinely cannot measure.
 */
export function gradeSpokenTranscript(
  expectedText: string,
  transcript: string,
  minWordMatch = 0.7,
): GradeResult {
  const diff = diffWords(expectedText, transcript)
  const expectedWords = diff.filter((d) => d.expected !== undefined).length
  const matched = diff.filter((d) => d.status === 'correct' || d.status === 'accent').length
  const score = expectedWords === 0 ? 0 : matched / expectedWords
  const correct = score >= minWordMatch

  return result(correct, score, {
    diff,
    expected: normalizeText(expectedText),
    message: correct
      ? 'Got it — the words and order are right.'
      : 'Some words are missing or different.',
  })
}
